<template>
  <div
    class="sr-profile"
    data-test="graphics-sportsradarStatSelector"
  >
    <!-- "Body" -->
    <div class="sr-profile__content">
      <div class="sr-profile__left sportradar-mapper">
        <div class="sportradar-mapper__scrollable">
          <p class="sr-profile__small-title">
            Select Preview Data
            <span class="sr-profile__small-subtitle">
              <span
                data-balloon="Preview purposes only. Data can be changed in Producer"
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span class="fas fa-question-circle" />
              </span>
            </span>
          </p>
          <label class="sr-profile__select">
            Season
            <span
              v-if="isLoading"
              class="far fa-spinner-third fa-spin"
              data-test="graphics-loader-season"
            />
            <select
              v-model="_seasonId"
              :disabled="isLoading"
              data-test="graphics-dd-season"
              data-entity="graphics-sportradar-selectSeason"
              @change="handleSeasonChange"
            >
              <option
                :value="null"
                disabled
              >Select Season</option>
              <option
                v-for="s in seasonsByYear"
                :key="s.seasonId"
                :data-test="`graphics-sportradar-optionSeason-${s.seasonId}`"
                :value="s.seasonId"
              >
                {{ s.seasonYear }} - {{ s.seasonTypeName }}
              </option>
            </select>
          </label>
          <label
            v-if="filterByConference"
            class="sr-profile__select"
          >
            Conference
            <span
              v-if="isLoading"
              class="far fa-spinner-third fa-spin"
              data-test="graphics-loader-conference"
            />
            <select
              v-model="_conferenceFilter"
              :disabled="isLoading"
              data-test="graphics-dd-conference"
              data-entity="graphics-sportsradar-selectConference"
              @change="handleConferenceChange($event.target.value)"
            >
              <option
                v-if="!isLoading"
                :value="null"
                disabled
              >Select Conference</option>
              <option :value="'all'">All Conferences</option>
              <option
                v-for="c in conferences"
                :key="c.id"
                :value="c.name"
                :data-test="`graphics-sportsradar-optionConference-${c.id}`"
              >
                {{ c.name }}
              </option>
            </select>
          </label>
          <label class="sr-profile__select">
            Team
            <span
              v-if="isLoading"
              class="far fa-spinner-third fa-spin"
              data-test="graphics-loader-team"
            />
            <select
              v-model="_teamId"
              :disabled="isLoading"
              data-test="graphics-dd-team"
              data-entity="graphics-sportradar-selectTeam"
              @change="handleTeamChange"
            >
              <option
                :value="null"
                disabled
              >Select Team</option>
              <option
                v-for="t in displayTeams"
                :key="t.teamId"
                :data-test:="`graphics-sportradar-optionTeam-${t.teamId}`"
                :value="t.teamId"
              >
                {{ t.teamDisplayName }}
              </option>
            </select>
          </label>
          <label
            v-if="statType === 'player'"
            class="sr-profile__select"
          >
            Player
            <span
              v-if="isLoading"
              class="far fa-spinner-third fa-spin"
              data-test="graphics-loader-player"
            />
            <select
              v-model="_playerId"
              :disabled="isLoading"
              data-test="graphics-dd-player"
              data-entity="graphics-sportradar-selectPlayer"
            >
              <option
                :value="null"
                disabled
              >Select Player</option>
              <option
                v-for="p in players"
                :key="p.playerId"
                :data-test="`graphics-sportradar-optionPlayer-${p.playerId}`"
                :value="p.playerId"
              >
                {{ $filters.playerDisplayName(p) }}
              </option>
            </select>
          </label>

          <label
            v-if="statPeriod === 'game'"
            class="sr-profile__select"
          >
            Game
            <span
              v-if="isLoading"
              class="far fa-spinner-third fa-spin"
              data-test="graphics-loader-game"
            />
            <select
              v-model="_gameId"
              :disabled="isLoading"
              data-test="graphics-dd-game"
              data-entity="graphics-sportradar-selectGame"
            >
              <option
                :value="null"
                disabled
              >Select Game</option>
              <option
                v-for="g in teamGames"
                :key="g.gameId"
                :value="g.gameId"
                :data-test="`graphics-sportradar-optionGame-${g.gameId}`"
              >
                {{ $filters.eventDisplayName(g) }}
              </option>
            </select>
          </label>

          <p class="sr-profile__small-title margin-top-lg">
            Data You Selected
          </p>
          <div
            v-for="(s, idx) in selectedData"
            :key="`${s.key}-${idx}`"
            class="sr-profile__selected-data"
          >
            <button
              class="btn btn-sm btn-link sr-profile__selected-data-remove"
              :data-test="`graphics-sportsradarStatSelector-remove-${s.key}`"
              @click="removeKey(idx)"
            >
              <span class="fas fa-times"></span>
            </button>
            <div>
              <p class="sr-profile__selected-data-key">
                {{ $filters.snakeToCapitalized(s.key) }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="sr-profile__right sportradar-mapper">
        <p class="sr-profile__small-title margin-top-lg">
          Select your data points for your template.
        </p>
        <p
          class="sr-profile__small-subtitle"
          :class="{
            'text-danger': statLimit > 0 && selectedStats.length === statLimit,
          }"
        >
          <span
            v-if="statLimit > 0"
            data-test="graphics-sportsradarStatSelector-dataPointLimit"
          >Choose up to {{ statLimit }} data points to start.</span>
          <span
            v-else
            data-test="graphics-sportsradarStatSelector-noDataPointLimit"
          >Choose some data points to get started.</span>
          <span
            data-balloon="Don’t worry. You can always add more later! We start you off with four data points so the data fits best into the pre-designed template."
            data-balloon-pos="down"
            data-balloon-length="large"
          >
            <span class="fas fa-question-circle"></span>
          </span>
        </p>
        <div class="google-connect-form__search margin-bottom-md">
          <input
            v-model="searchTerm"
            type="text"
            class="mx-input"
            placeholder="Search..."
            data-test="graphics-sportsradarStatSelector-search"
          />
          <span class="fas fa-search" />
        </div>
        <div class="sportradar-mapper__scrollable margin-top-lg">
          <key-mapper
            v-model="selectedStats"
            :data="formattedSchema"
            multiple
            :limit="statLimit > 0 ? statLimit : undefined"
            :force-expand="!!searchTerm"
          />
        </div>
      </div>
    </div>
    <div
      class="flex"
      :class="{
        'flex-space-between': allowBack,
        'flex-end' : !allowBack,
      }"
    >
      <button
        v-if="allowBack"
        class="btn btn-link"
        data-test="graphics-sportsradarStatSelector-back"
        data-entity="graphics-sportsradarStatSelector-back"
        :disabled="backButtonDisabled"
        @click="previous"
      >
        Back
      </button>
      <button
        class="btn btn-style-a"
        :disabled="addButtonDisabled"
        data-test="graphics-sportsradarStatSelector-addData"
        data-entity="graphics-sportsradarStatSelector-addData"
        @click="next"
      >
        <span
          v-if="isAddingData"
          class="far fa-spinner-third fa-spin"
        ></span>
        <span v-else>Add Data</span>
      </button>
    </div>
  </div>
</template>

<script>

import { mapActions, mapState } from 'vuex';
import { pick } from 'lodash-es';
import Fuse from 'fuse.js';
import sportradarMixin from '../../../mixins/sportradar-mixin';
import leagueInfo from '../../../utils/leagueInfo';
import KeyMapper from '../../SettingsPanel/Data/Sportradar/KeyMapper.vue';
import { resolverNames } from '../../../utils/sportradarConfigs';

import flatten from 'flat';

const { unflatten } = flatten;

export default {
  emits: ['previous-step', 'next-step'],
  name: 'StatSelector',
  components: {
    KeyMapper,
  },
  mixins: [sportradarMixin],
  props: {
    statType: { // player/team
      type: String,
      required: true,
    },
    statPeriod: { // game/season
      type: String,
      required: true,
    },
    providerId: {
      type: String,
      required: true,
    },
    allowBack: {
      type: Boolean,
      default: true,
    },
    statLimit: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      selectedStats: [],
      searchTerm: '',
      loadingStack: [],
      isAddingData: false,
      isFetchingInitialData: false,
      displayTeams: [],
    };
  },
  computed: {
    ...mapState({
      playerSeasonStatsSchema(state) {
        if (leagueInfo[this.leagueName]?.statsProvider === 'sportsstack') {
          return state[this.providerId].schemaInfo[`PlayerStatistics${this.leagueName.toUpperCase()}`];
        }
        return {
          total: state[this.providerId].schemaInfo[`PlayerSeasonStatsTotal${this.leagueName.toUpperCase()}`],
          average: state[this.providerId].schemaInfo[`PlayerSeasonStatsAverage${this.leagueName.toUpperCase()}`],
        };
      },
      teamSeasonStatsSchema(state) {
        if (leagueInfo[this.leagueName]?.statsProvider === 'sportsstack') {
          return state[this.providerId].schemaInfo[`TeamStatistics${this.leagueName.toUpperCase()}`];
        }
        const statsTotalSchemaState = state[this.providerId].schemaInfo[`TeamSeasonStatsTotal${this.leagueName.toUpperCase()}`];
        const statsAverageSchemaState = state[this.providerId].schemaInfo[`TeamSeasonStatsAverage${this.leagueName.toUpperCase()}`];
        return {
          opponentsTotal: statsTotalSchemaState,
          opponentsAverage: statsAverageSchemaState,
          teamTotal: statsTotalSchemaState,
          teamAverage: statsAverageSchemaState,
        };
      },
      teamGameStatsSchema(state) {
        if (leagueInfo[this.leagueName]?.statsProvider === 'sportsstack') {
          return state[this.providerId].schemaInfo[`TeamStatistics${this.leagueName.toUpperCase()}`];
        }
        return state[this.providerId].schemaInfo[`TeamGameStatistics${this.leagueName.toUpperCase()}`];
      },
      playerGameStatsSchema(state) {
        if (leagueInfo[this.leagueName]?.statsProvider === 'sportsstack') {
          return state[this.providerId].schemaInfo[`PlayerStatistics${this.leagueName.toUpperCase()}`];
        }
        return state[this.providerId].schemaInfo[`PlayerGameStats${this.leagueName.toUpperCase()}`];
      },
      sportradarData(state, getters) {
        return getters[`${this.providerId}/allSportradarData`];
      },
      leagueName(state) {
        return state[this.providerId].leagueName;
      },
      seasons(state) {
        return state[this.providerId].seasons;
      },
      seasonsByYear(state, getters) {
        return getters[`${this.providerId}/seasonsByYear`];
      },
      Season(state) {
        return state[this.providerId].Season;
      },
      teams(state) {
        return state[this.providerId].teams;
      },
      Team(state) {
        return state[this.providerId].Team;
      },
      statistics(state) {
        return state[this.providerId].statistics;
      },
      players(state) {
        return state[this.providerId].players;
      },
      Player(state) {
        return state[this.providerId].Player;
      },
      Game(state) {
        return state[this.providerId].Game;
      },
      games(state) {
        return state[this.providerId].games;
      },
      ConferenceFilter(state) {
        return state[this.providerId].conferenceFilter;
      },
      filterByConference() {
        return leagueInfo[this.leagueName]?.additionalFilters?.includes('conference');
      },
      conferences() {
        const teams = Object.values(this?.teams || {});
        if (!teams?.length || !this.filterByConference) {
          return [];
        }

        // Dedupe conferences
        const conferenceMap = new Map();
        teams.forEach((team) => {
          const conference = team?.Conference;
          if (conference && !conferenceMap.has(conference.conferenceId)) {
            conferenceMap.set(conference.conferenceId, {
              name: conference.conferenceName,
              id: conference.conferenceId,
            });
          }
        });

        // Sort by conference name
        return Array.from(conferenceMap.values()).sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
      },
    }),
    _seasonId: {
      get() {
        return this.Season?.seasonId;
      },
      set(v) {
        const season = this.seasons[v];
        this.$store.commit(`${[this.providerId]}/SET_SEASON`, season);
      },
    },
    _teamId: {
      get() {
        return this.Team?.teamId;
      },
      set(v) {
        this.$store.commit(`${[this.providerId]}/SET_TEAM`, this.teams[v]);
      },
    },
    _playerId: {
      get() {
        return this.Player?.playerId;
      },
      set(v) {
        const player = this.players.find((p) => p.playerId === v);
        this.$store.commit(`${[this.providerId]}/SET_PLAYER`, player);
      },
    },
    _gameId: {
      get() {
        return this.Game?.gameId;
      },
      set(v) {
        const game = this.games.find((g) => g.gameId === v);
        this.$store.commit(`${[this.providerId]}/SET_GAME`, game);
      },
    },
    _conferenceFilter: {
      get() {
        return this.ConferenceFilter || 'all';
      },
      set(v) {
        this.$store.commit(`${[this.providerId]}/SET_CONFERENCE_FILTER`, v);
      },
    },
    isLoading() {
      return this.loadingStack.length > 0 || this.isFetchingInitialData;
    },
    statsProviderIsSportsStack() {
      return leagueInfo[this.leagueName]?.statsProvider === 'sportsstack';
    },
    addButtonDisabled() {
      if (this.statType === 'team') {
        return !this._teamId || this.isLoading || this.isAddingData;
      }
      return !this._playerId || this.isLoading || this.isAddingData;
    },
    backButtonDisabled() {
      return this.isAddingData;
    },
    jumpPath() {
      // TODO: remove/replace resolverNames in UI selection, only use for gql and stats data
      if (this.statType === 'team' && this.statPeriod === 'season') {
        if (this.statsProviderIsSportsStack) {
          return `${resolverNames.TeamSeasonStats}.statistics`;
        }
        return `${resolverNames.TeamSeasonStats}`;
      }
      if (this.statType === 'team' && this.statPeriod === 'game') {
        return `${resolverNames.TeamGameStats}.statistics`;
      }
      if (this.statType === 'player' && this.statPeriod === 'season') {
        if (this.statsProviderIsSportsStack) {
          return `${resolverNames.PlayerSeasonStats}.statistics`;
        }
        return `${resolverNames.PlayerSeasonStats}`;
      }
      if (this.statType === 'player' && this.statPeriod === 'game') {
        return `${resolverNames.PlayerGameStats}.statistics`;
      }
      return '';
    },
    relevantSchema() {
      const { statPeriod, statType } = this;
      if (statPeriod === 'season' && statType === 'team') return this.teamSeasonStatsSchema;
      if (statPeriod === 'game' && statType === 'team') return this.teamGameStatsSchema;
      if (statPeriod === 'season' && statType === 'player') return this.playerSeasonStatsSchema;
      if (statPeriod === 'game' && statType === 'player') return this.playerGameStatsSchema;
      return {};
    },
    searchedSchema() {
      if (this.searchTerm) {
        // Do search
        const flatData = flatten(this.relevantSchema);
        const keys = new Fuse(Object.keys(flatData), {
          threshold: 0.2,
        })
          .search(this.searchTerm)
          .map((r) => r.item);
        const searchedObj = pick(flatData, keys);
        return unflatten(searchedObj);
      }

      return this.relevantSchema;
    },
    formattedSchema() {
      return this.formatForKeyMapper(this.searchedSchema, this.relevantSchema);
    },
    teamGames() {
      const isPlayersPool = leagueInfo[this.leagueName]?.participantType === 'players_pool';
      return (isPlayersPool)
        ? this.games
        : this.games?.filter((g) => [g.homeTeamId, g.awayTeamId]?.includes(this._teamId)); // eslint-disable-line no-underscore-dangle, max-len
    },
    selectedData() {
      const jumpPathLength = this.jumpPath.split('.').length;
      return this.selectedStats.map((selectedKey) => ({
        key: selectedKey.split('.').pop(),
        path: selectedKey
          .split('.')
          .slice(jumpPathLength)
          .map((k) => k)
          .join('/'),
      }));
    },
    sortedTeamsByDisplayName() {
      const teams = Object.values(this?.teams || {});
      if (!teams?.length) return [];
      return teams?.sort((a, b) => {
        if (a.teamDisplayName < b.teamDisplayName) return -1;
        if (a.teamDisplayName > b.teamDisplayName) return 1;
        return 0;
      });
    },
  },
  async created() {
    this.isFetchingInitialData = true;
    const { statPeriod, statType } = this;
    await this.getStatSelectionData({
      newElement: true,
      statPeriod,
      statType,
    });
    this.displayTeams = this.sortedTeamsByDisplayName;
    this.isFetchingInitialData = false;
  },
  methods: {
    ...mapActions({
      getTeams(dispatch, payload) {
        return dispatch(`${this.providerId}/getTeams`, payload);
      },
      getSchedule(dispatch, payload) {
        return dispatch(`${this.providerId}/getSchedule`, payload);
      },
      getStatSelectionData(dispatch, payload) {
        return dispatch(`${this.providerId}/getStatSelectionData`, payload);
      },
      statSelectorChange(dispatch, payload) {
        return dispatch(`${this.providerId}/statSelectorChange`, payload);
      },
      hydrateStatsData(dispatch, payload) {
        return dispatch(`${this.providerId}/syncGqlDataFromElement`, payload);
      },
    }),
    async _statSelectorChange({ statPeriod, statType, conferenceFilter }, selectionType) {
      let teamId = this._teamId;
      if (selectionType === 'season' && (!teamId || teamId === 'free_agent')) {
        // If no team is selected or the selected team is 'free_agent' choose the first team.
        teamId = Object.values(this.teams)[0].teamId;
      }

      try {
        return await this.statSelectorChange({
          leagueName: this.leagueName,
          teamId,
          seasonId: this._seasonId,
          statPeriod,
          statType,
          conferenceFilter,
          selectionType,
        });
      } catch (error) {
        console.error('Error getting stats modal data', error);
      }
    },
    async handleSeasonChange() {
      if (this.isFetchingInitialData) {
        return;
      }
      this.loadingStack.push(1);
      const { statPeriod, statType } = this;
      await this._statSelectorChange({ statPeriod, statType }, 'season');
      this.displayTeams = this.sortedTeamsByDisplayName;
      this.loadingStack.pop();
    },
    async handleTeamChange() {
      if (this.isFetchingInitialData) {
        return;
      }
      this.loadingStack.push(1);
      const { statPeriod, statType } = this;
      // Only get new data if we need updated games or players
      if (this.statType === 'player' || this.statPeriod === 'game') {
        await this._statSelectorChange({ statPeriod, statType }, 'team');
      }

      if (!Object.values(this.players).length) {
        // no player data, maybe a team that missed the post season
        // change to regular season
        this._season = this.seasonsByYear[0];
      } else if (
        !this._playerId
        || !Object.values(this.players).some((p) => p.playerId === this._playerId)
      ) {
        this.Player = Object.values(this.players)[0];
      }

      this.loadingStack.pop();
    },
    async handleConferenceChange(v) {
      const { statPeriod, statType } = this;
      await this._statSelectorChange({ statPeriod, statType, conferenceFilter: v },'conference'); // eslint-disable-line
      if (!v || v === 'all') {
        this.displayTeams = this.sortedTeamsByDisplayName;
        return;
      }
      this.displayTeams = this.sortedTeamsByDisplayName.filter(
        (team) => team.Conference?.conferenceName === v,
      );
      this._teamId = this.displayTeams[0]?.teamId || null; // eslint-disable-line
      this.handleTeamChange();
    },
    removeKey(idx) {
      this.selectedStats.splice(idx, 1);
    },
    previous() {
      this.$emit('previous-step');
    },
    async next() {
      this.isAddingData = true;
      await this.hydrateStatsData({
        playerId: this._playerId,
        statType: this.statType,
        statPeriod: this.statPeriod,
        leagueName: this.leagueName,
        seasonId: this._seasonId,
        teamId: this._teamId,
        gameId: this._gameId,
      });

      this.$emit('next-step', {
        stats: this.selectedStats.map((p) => `${this.jumpPath}.${p}`),
      });
      this.isAddingData = false;
    },
  },
};
</script>
